<template>
    <section
        :data-mui-theme="hasBackground ? theme : undefined"
        class="slice slice-tags slice-tags--default"
        aria-label="tags"
        data-testid="slice-tags-default"
    >
        <div class="slice-content" :class="{'mui-background-lighter': hasBackground}">
            <div class="slice-tags--default__header">
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.title)"
                    :field="slice.primary.title"
                    class="mui-title"
                    data-testid="slice-tags-default-title"
                />
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.description)"
                    :field="slice.primary.description"
                    class="slice-tags--default__description slice-description"
                    data-testid="slice-tags-default-description"
                />
            </div>

            <div v-if="slice.primary.items?.length" class="slice-tags--default__list" data-testid="slice-tags-default-list">
                <Component
                    :is="isFilled.link(item.link) ? NuxtLink : 'div'"
                    v-for="(item, index) in slice.primary.items"
                    :key="index"
                    :to="isFilled.link(item.link) && item.link.url ? $withLocalePrefix(item.link.url) : undefined"
                    :class="['slice-tags--default__item', {'slice-tags--default__item--has-link': isFilled.link(item.link)}]"
                    :data-testid="`slice-tags-default-item-${index}`"
                >
                    <PrismicImage
                        v-if="isFilled.image(item.image)"
                        :field="item.image"
                        :alt="item.image.alt ?? ''"
                        class="slice-tags--default__item-image"
                        :data-testid="`slice-tags-default-item-image-${index}`"
                    />
                    <span
                        v-if="isFilled.keyText(item.tag)"
                        class="slice-tags--default__item-label"
                        :data-testid="`slice-tags-default-item-label-${index}`"
                    >
                        {{ item.tag }}
                    </span>
                </Component>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {TagsSlice} from '@prismicio-types';
    import {NuxtLink} from '#components';

    type TagsDefaultInZone = Extract<TagsSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<TagsDefaultInZone>());
    const {slice} = props;

    const theme = computed(() => slice.primary.theme);
    const hasBackground = computed(() => theme.value !== 'none');
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-tags--default {
        .slice-content {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-8);
            padding: var(--joy-core-spacing-8) var(--joy-core-spacing-5);
            border-radius: var(--joy-core-radius-5);

            @include mq.screen_md(min) {
                padding: var(--joy-core-spacing-12);
            }
        }

        &__header {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: var(--joy-core-spacing-2);
            text-align: center;
        }

        &__description {
            margin: 0;
            color: var(--joy-color-text-default);

            h3,
            h4,
            p {
                margin: 0;
                @include text.getFontBySize(primary-400);
            }
        }

        &__list {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: var(--joy-core-spacing-4);
        }

        &__item {
            display: inline-flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);
            padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);
            background-color: var(--joy-color-background-default);
            border-radius: var(--joy-core-radius-4);
            box-shadow: var(--joy-core-elevation-2);
            text-decoration: none;
            color: inherit;
        }

        &__item--has-link {
            cursor: pointer;
        }

        &__item-image {
            display: block;
            width: 32px;
            height: 32px;
            object-fit: contain;
            flex-shrink: 0;
        }

        &__item-label {
            @include text.getFontBySize(primary-450);
            color: var(--joy-color-neutral-70);
            white-space: nowrap;
        }
    }
</style>
